import { useRouteLoaderData } from "react-router"
import { DocsRouterLink as RouterLink, DocsUiLink as Link } from "~/docs-data/docs-link"
import {
  MapPinIcon,
  VectorSquareIcon,
  SplitIcon,
  type LucideIcon,
} from "lucide-react"
import { H1, P } from "~/components/Typography"
import { canonicalMetricSlugForType, metricCanonicalName } from "~/docs-data/helpers"
import type { DocsCatalog } from "~/server/models/docsCatalog.server"

const ICON_BY_TYPE: Record<string, LucideIcon> = {
  place: MapPinIcon,
  area: VectorSquareIcon,
  way: SplitIcon,
}

// Backend returns location types alphabetically; the docs UX shows
// place → area → way.
const LT_ORDER: Record<string, number> = { place: 0, area: 1, way: 2 }

type MetricLink = { to: string; label: string }

/**
 * "All metrics" landing page (`/docs/concepts/metrics`). Lists every metric
 * concept grouped by location type. Mirrors the concept-collapsing used in the
 * Concepts sidebar and mega menu: cadence variants and differently-prefixed
 * LTMs that share a canonical display name fold into a single entry, linking to
 * the concrete LTM page (single-variant) or the typed canonical overview
 * (multi-variant).
 */
export default function MetricsIndex() {
  const docs = useRouteLoaderData("routes/_console.docs") as
    | DocsCatalog
    | undefined
  const datasets = docs?.datasets ?? []
  const locationTypes = [...(docs?.locationTypes ?? [])].sort((a, b) => {
    const ao = LT_ORDER[a.id] ?? 99
    const bo = LT_ORDER[b.id] ?? 99
    return ao - bo || a.id.localeCompare(b.id)
  })

  const metricsForType = (locationType: string): MetricLink[] => {
    const seenLTMs = new Set<string>()
    const rows: { ltm: string; name: string }[] = []
    for (const d of datasets) {
      if (d.locationType !== locationType) continue
      if (seenLTMs.has(d.ltm)) continue
      seenLTMs.add(d.ltm)
      rows.push({ ltm: d.ltm, name: d.name })
    }
    const byConcept = new Map<string, typeof rows>()
    const conceptOrder: string[] = []
    for (const r of rows) {
      const concept = metricCanonicalName(r.name)
      if (!byConcept.has(concept)) {
        byConcept.set(concept, [])
        conceptOrder.push(concept)
      }
      byConcept.get(concept)!.push(r)
    }
    conceptOrder.sort((a, b) => a.localeCompare(b))
    return conceptOrder.map((concept) => {
      const members = byConcept.get(concept)!
      const to =
        members.length === 1
          ? `/docs/concepts/metrics/${members[0].ltm}`
          : `/docs/concepts/metrics/${canonicalMetricSlugForType(concept, locationType)}`
      return { to, label: concept }
    })
  }

  const groups = locationTypes
    .map((lt) => ({ lt, metrics: metricsForType(lt.id) }))
    .filter((g) => g.metrics.length > 0)

  return (
    <div className="max-w-screen-xl">
      <H1>All metrics</H1>
      <P>
        Every metric concept in the catalog, grouped by location type. Each metric is
        scoped to exactly one location type. See{" "}
        <Link to="/docs/concepts" variant="inline">Concepts</Link> for what the location
        types mean, and{" "}
        <Link to="/docs/datasets" variant="inline">Datasets</Link> for which countries each
        metric is available in.
      </P>

      {groups.length === 0 ? (
        <p className="mt-8 text-sm italic text-muted-foreground">
          No metrics catalogued yet.
        </p>
      ) : (
        groups.map(({ lt, metrics }) => {
          const Icon = ICON_BY_TYPE[lt.id]
          return (
            <section key={lt.id} className="mt-10">
              <div className="mb-4 flex items-center gap-2">
                {Icon && (
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                )}
                <h2 className="text-xl font-semibold text-foreground">
                  {lt.displayName || lt.id}
                </h2>
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {metrics.length}
                </span>
              </div>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {metrics.map((m) => (
                  <RouterLink
                    key={m.to}
                    to={m.to}
                    className="group rounded-lg border bg-card p-4 transition-all hover:border-primary hover:shadow-sm"
                  >
                    <span className="text-sm font-medium text-foreground group-hover:text-primary">
                      {m.label}
                    </span>
                  </RouterLink>
                ))}
              </div>
            </section>
          )
        })
      )}
    </div>
  )
}
